import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { OrderProcesStore } from 'src/app/+shared/stores/order-process.store';
import { OrderProcessData } from '../../jurisdiction-overview/jurisdiction-overview.domain';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { UserService } from 'src/app/services/user.service';
import { SkExecutorAction } from 'src/app/+shared/components/review-section/review-section.domain';
import { OrderProcessService } from 'src/app/jurisdiction/order-process/order-process.service';
import { ToastNotificationService } from 'src/app/+shared/services/toast-notification.service';
import { AllDropdownKeys } from 'src/app/jurisdiction/jurisdiction.constants';
import { ReviewSectionSelectors } from 'src/app/+shared/components/review-section/review-section.selectors';
import { Observable, of } from 'rxjs';
import { ConfirmationService } from 'primeng';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { ErrorService } from 'src/app/services/error/error.service';

@Component({
  selector: 'app-order-process',
  templateUrl: './order-process.component.html',
  styleUrls: ['./order-process.component.scss'],
})
export class OrderProcessComponent extends DestroyableComponent implements OnInit {
  public orderProcess: OrderProcessData;
  public showApproveApplicationBtn: boolean;
  public showRejectApplicationBtn: boolean;
  public showRequestUpdateApplicationBtn: boolean;
  public showUpdateApplicationBtn: boolean;

  public areAllSectionsApproved$: Observable<boolean> = of(false);
  public areAnySectionsRejected$: Observable<boolean> = of(false);
  public areAnySectionsWithoutReview$: Observable<boolean> = of(false);

  public SkExecutorAction = SkExecutorAction;
  public AllDropdownKeys = AllDropdownKeys;

  @Output() onOrderProcessUpdated = new EventEmitter<void>();
  @Output() updateApplication = new EventEmitter<{ executorAction: string }>();

  constructor(
    private orderProcessStore: OrderProcesStore,
    private userService: UserService,
    private orderProcessService: OrderProcessService,
    private notificationsService: ToastNotificationService,
    private reviewSelectors: ReviewSectionSelectors,
    private confirmationService: ConfirmationService,
    private translationService: NetzTranslateService,
    private errorService: ErrorService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getOrderProcess(orderProcess => {
      const applicationId = parseInt(orderProcess.application_id?.toString());
      this.areAllSectionsApproved$ = this.reviewSelectors.areAllSectionsApproved(applicationId);
      this.areAnySectionsRejected$ = this.reviewSelectors.areAnySectionsRejected(applicationId);
      this.areAnySectionsWithoutReview$ = this.reviewSelectors.areAnySectionsWithoutReview(applicationId);
    });
  }

  public updateOrderProcess(executorAction: string, options?: { onUpdating$: Observable<any> }) {
    const getConfirmationMessage = (action: string): string => {
      if (action === SkExecutorAction.update_application) {
        return this.translationService.translateString('overview.process-details.confirmation-text-update-application');
      } else if (action === SkExecutorAction.request_update_application) {
        return this.translationService.translateString('overview.process-details.confirmation-text-request-change');
      } else if (action === SkExecutorAction.application_accepted) {
        return this.translationService.translateString('overview.process-details.confirmation-text-accept-application');
      } else if (action === SkExecutorAction.application_rejected) {
        return this.translationService.translateString('overview.process-details.confirmation-text-reject-application');
      }
    };

    const updateAction = () =>
      this.preventLeak(
        this.orderProcessService.updateOrderProcessData(this.orderProcess.orderprocess_id, {
          sk_executor_action: executorAction,
        })
      ).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
          this.notificationsService.success(this.translationService.translateString('app.ui-messages.success'));
          setTimeout(() => {
            this.onOrderProcessUpdated.emit();
          }, 2000); // wait for 2000 seconds, because on the backend side the request is processed asynchronously by a workflow. We need websockets to handle this properly
        },
        error => this.errorService.throwErrorIfFailure(error, true)
      );

    this.confirmationService.confirm({
      message: getConfirmationMessage(executorAction),
      accept: () => {
        if (!options?.onUpdating$) {
          updateAction();
          return;
        }

        this.preventLeak(options.onUpdating$).subscribe(() => {
          updateAction();
        });
      },
      reject: () => this.confirmationService.close(),
    });
  }

  private getOrderProcess(onSuccess?: (OrderProcessData: OrderProcessData) => void) {
    this.preventLeak(this.orderProcessStore.getOrderProcessValue()).subscribe(orderProcess => {
      this.orderProcess = orderProcess;

      onSuccess && orderProcess && onSuccess(orderProcess);

      if (orderProcess) {
        this.setButtonsVisibility(orderProcess);
      }
    });
  }

  private setButtonsVisibility(orderProcess: OrderProcessData) {
    this.showApproveApplicationBtn =
      this.userService.isUserModeratorRole() &&
      orderProcess.sk_executor_action === SkExecutorAction.accept_application &&
      this.isExecutorRole(orderProcess);

    this.showRejectApplicationBtn =
      orderProcess.sk_executor_action === SkExecutorAction.accept_application && this.isExecutorRole(orderProcess);

    this.showRequestUpdateApplicationBtn =
      orderProcess.sk_executor_action === SkExecutorAction.accept_application && this.isExecutorRole(orderProcess);

    this.showUpdateApplicationBtn =
      orderProcess.sk_executor_action === SkExecutorAction.update_application &&
      this.isExecutorRole(orderProcess) &&
      this.isApplicationOwner(orderProcess);
  }

  private isApplicationOwner(orderProcess: OrderProcessData): boolean {
    return this.userService.userId?.toString() == orderProcess.application_user_id;
  }

  private isExecutorRole(orderProcess: OrderProcessData): boolean {
    return orderProcess.executor
      .split(',')
      .map(x => x.trim())
      .includes(this.userService.getUserRole());
  }
}
